import { useCallback } from 'react';

import { useDeleteTicket } from '@givebacks/api/store';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, Group, Modal, Stack, Text, notifications } from 'ui';

type DeleteTicketModalProps = {
  opened: boolean;
  onClose: () => void;
  ticketId: string;
  causeId: string;
  campaignId: string;
  onSuccess?: () => void;
  isSeasonTicket?: boolean;
};

const DeleteTicketModalContent = ({
  onClose,
  ticketId,
  causeId,
  campaignId,
  onSuccess,
  isSeasonTicket,
}: {
  onClose: () => void;
  ticketId: string;
  causeId: string;
  campaignId: string;
  onSuccess?: () => void;
  isSeasonTicket?: boolean;
}) => {
  const { mutate: deleteTicket, isPending: isLoading } = useDeleteTicket({
    ticketsType: isSeasonTicket ? 'season_passes' : 'tickets',
  });
  const handleDelete = useCallback(() => {
    if (!causeId || !campaignId) return;
    deleteTicket(
      {
        ticketId,
        eventId: campaignId,
        causeId,
      },
      {
        onSuccess: () => {
          notifications.show({
            message: 'Ticket deleted successfully',
            variant: 'success',
          });
          onSuccess?.();
          onClose();
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error, 'Failed to delete ticket'),
            variant: 'error',
          });
        },
      }
    );
  }, [campaignId, causeId, deleteTicket, onClose, onSuccess, ticketId]);

  return (
    <Stack>
      <Text fw={500}>Are you sure you want to delete this ticket?</Text>
      <Group justify="flex-end">
        <ButtonMantine size="md" onClick={onClose} variant="outline">
          Cancel
        </ButtonMantine>
        <ButtonMantine size="md" color="cerise" onClick={handleDelete} loading={isLoading}>
          Delete Ticket
        </ButtonMantine>
      </Group>
    </Stack>
  );
};

const DeleteTicketModal = ({
  opened,
  onClose,
  ticketId,
  causeId,
  campaignId,
  onSuccess,
  isSeasonTicket,
}: DeleteTicketModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Delete ${isSeasonTicket ? 'Season Ticket' : 'Ticket Type'}`}
    >
      <DeleteTicketModalContent
        onClose={onClose}
        ticketId={ticketId}
        causeId={causeId}
        campaignId={campaignId}
        onSuccess={onSuccess}
        isSeasonTicket={isSeasonTicket}
      />
    </Modal>
  );
};

export { DeleteTicketModal };
